import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { stripeContract } from "@common/contracts/stripe.contract";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { integrationConnectionWithAccountsResponseSchema } from "@common/schemas/domains/integrationConnection/integration-connection-response.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { addIntegrationConnectionInQueryData } from "@queries/entitiesAccounts";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateAccountRelatedQueries } from "./account.api";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const stripeContractClient = getBackendAPIClient(stripeContract);

interface UseStripeConnectionCreationMutationOptions {
    onSuccess?: (connection: IntegrationConnection) => void;
    toastMessage?: string;
}

export function useStripeConnectionCreationMutation({
    onSuccess,
    toastMessage = "Stripe account connected",
}: UseStripeConnectionCreationMutationOptions = {}) {
    const { workspaceId, isOrgConnect, refreshConnections } =
        useConnectTarget();
    const { updateConnectAccountState } = useConnectAccount();
    const { toast } = useToaster();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof stripeContract.createConnection
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId && !isOrgConnect) {
                throw new Error("No active workspace");
            }

            const response = await stripeContractClient.createConnection({
                body: {
                    ...data,
                    workspaceId,
                },
            });

            return integrationConnectionWithAccountsResponseSchema.parse(
                response.body,
            );
        },
        onMutate: () => {
            updateConnectAccountState({
                isConnecting: true,
            });
        },
        onSuccess: async (connection) => {
            addIntegrationConnectionInQueryData(queryClient, connection);
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (a) => !a.financialAccount?.entityId,
                );

            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    integrationConnection: connection,
                });
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
                toast(toastMessage);
            }

            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            invalidateAccountRelatedQueries({ workspaceId });
            await refreshConnections({
                provider: "stripe",
                connectionId: String(connection.id),
            });
            onSuccess?.(connection);
        },
        onError: () => {
            updateConnectAccountState({
                isConnecting: false,
            });
        },
    });
}
